import { Button, ButtonProps, useDisclosure } from "@chakra-ui/react";
import { assertNever } from "@syla/shared/types/helpers/errors";
import { DataSourceCode } from "@syla/shared/types/models/DataSourceBase";
import { AddWalletType } from "@syla/shared/types/requests/AddWalletRequest";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { captureRequestError } from "../../../../helper/captureRequestError";
import { getAuthoriseUrl } from "../../../../oAuth/getAuthoriseUrl";
import { addWallet, deleteWallet } from "../../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import {
  WindowOpenFailedError,
  openOAuthWindow,
} from "../helpers/openOAuthWindow";
import { AuthoriseConnectionModal } from "./AuthoriseConnectionModal";

/**
 * Use for Sync flow which requires a 'connect' process like OAuth.
 */
export function AuthoriseConnectionButton({
  walletId,
  dataSource,
  customName,
  onAuthorised,
  onError,
  ...props
}: {
  walletId?: string; // provided when there is an existing wallet, otherwise a new wallet will be created on auth.
  dataSource: DataSourceWithNetworks;
  customName: string | undefined;
  onAuthorised: (walletId: string) => void;
  onError: (error: Error) => void;
} & Omit<ButtonProps, "onError">) {
  const {
    authorise,
    authInProgress,
    disclosure,
    focusAuthoriseWindow,
    cancelAuthorisation,
  } = useOAuthAuthorise({
    walletId,
    dataSource,
    customName,
    onAuthorised,
    onError,
  });

  return (
    <>
      <Button
        colorScheme="primary"
        onClick={authorise}
        isLoading={authInProgress}
        isDisabled={authInProgress}
        {...props}
      >
        Connect
      </Button>
      {disclosure.isOpen && (
        <AuthoriseConnectionModal
          disclosure={disclosure}
          onCancel={cancelAuthorisation}
          onContinue={focusAuthoriseWindow}
        />
      )}
    </>
  );
}

function useOAuthAuthorise({
  dataSource,
  customName,
  walletId,
  onAuthorised,
  onError,
}: {
  dataSource: DataSourceWithNetworks;
  customName?: string;
  walletId?: string;
  onAuthorised: (walletId: string) => void | Promise<void>;
  onError: (error: Error) => void;
}) {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const disclosure = useDisclosure();

  const [authInProgress, setAuthInProgress] = useState(false);
  const authoriseWindowRef = useRef<Window>();

  const newWalletIdRef = useRef<string>();

  const setNewWalletId = useCallback((walletId: string | undefined) => {
    newWalletIdRef.current = walletId;
  }, []);

  const revertNewWallet = useCallback(() => {
    const walletId = newWalletIdRef.current;

    if (!walletId) {
      return;
    }

    newWalletIdRef.current = undefined;

    deleteWallet(accountId, walletId).catch((error) => {
      captureRequestError(error);
    });
  }, [accountId]);

  const cleanup = useCallback(() => {
    disclosure.onClose();
    authoriseWindowRef.current?.close();
    authoriseWindowRef.current = undefined;
    setAuthInProgress(false);
    // should newWalletId be cleared here? setNewWalletId(undefined);
  }, [disclosure]);

  const cancelAuthorisation = useCallback(() => {
    revertNewWallet();
    cleanup();
  }, [cleanup, revertNewWallet]);

  const focusAuthoriseWindow = useCallback(() => {
    authoriseWindowRef.current?.focus();
  }, []);

  // if the component unmounts, it will close the window
  useEffect(() => {
    return () => {
      authoriseWindowRef.current?.close();
    };
  }, []);

  const authorise = useCallback(async () => {
    const dataSourceCode = dataSource.code;

    if (!dataSourceCode) {
      throw new Error("dataSourceCode expected");
    }

    if (
      !(
        dataSourceCode === DataSourceCode.Coinbase ||
        dataSourceCode === DataSourceCode.CoinJar
      )
    ) {
      throw new Error(`OAuth not supported for ${dataSourceCode}`);
    }

    setAuthInProgress(true);

    try {
      // The current implementation requires a wallet to exist before auth, so
      // there is somewhere for the auth details to be stored. This is not ideal,
      // as we can sometimes end up with a wallet being created, stuck in a
      // syncing state.
      // It would be preferable to manage this in the B/E.

      let currentWalletId = walletId;

      if (!currentWalletId) {
        const newWallet = await addWallet(accountId, {
          dataSourceId: dataSource._id,
          type: AddWalletType.Sync,
          customName,
        });

        currentWalletId = newWallet._id;
        setNewWalletId(currentWalletId);
      }

      const authoriseUrl = getAuthoriseUrl({
        accountId,
        dataSourceCode,
        walletId: currentWalletId,
      });

      const oauthWindow = openOAuthWindow({
        authoriseUrl,
        windowName: "OAuth",
        width: 500,
        height: 600,
      });

      authoriseWindowRef.current = oauthWindow.window;

      disclosure.onOpen();

      const result = await oauthWindow.result;

      switch (result.status) {
        case "success":
          cleanup();
          onAuthorised(currentWalletId);
          return;

        case "error":
          revertNewWallet();
          cleanup();
          onError(result.error);
          return;

        case "denied":
        case "closed":
          revertNewWallet();
          cleanup();
          return;

        default:
          assertNever(result);
      }
    } catch (error) {
      cleanup();

      if (!(error instanceof WindowOpenFailedError)) {
        captureRequestError(error);
      }

      if (error instanceof Error) {
        onError(error);
        return;
      }

      throw error;
    }
  }, [
    accountId,
    cleanup,
    customName,
    dataSource,
    onAuthorised,
    onError,
    disclosure,
    walletId,
    revertNewWallet,
    setNewWalletId,
  ]);

  return {
    authorise,
    authInProgress,
    disclosure,
    focusAuthoriseWindow,
    cancelAuthorisation,
  };
}
